import { z } from 'zod';

/** One screenshot in the feed: a short-lived presigned S3 GET plus the
 *  admin-authored caption shown under the fullscreen lightbox view. */
export const ChangelogFeedScreenshotSchema = z.object({
    url: z.string(),
    caption: z.string().nullable().default(null),
});

/** One published changelog entry, as served by GET /changelog/feed.
 *  body_html is trusted server-authored HTML (rendered with [innerHTML],
 *  Angular-sanitized); is_new is true when the entry is newer than the viewer's
 *  Seen watermark. `screenshots` carries per-image captions; the legacy
 *  `screenshot_urls` field backfills it (captionless) when an older API
 *  responds without it. */
export const ChangelogFeedEntrySchema = z
    .object({
        id: z.number(),
        title: z.string(),
        published_date: z.string(),
        body_html: z.string(),
        screenshot_urls: z.array(z.string()).default([]),
        screenshots: z.array(ChangelogFeedScreenshotSchema).optional(),
        is_new: z.boolean(),
    })
    .transform(entry => ({
        ...entry,
        screenshots: entry.screenshots ?? entry.screenshot_urls.map(url => ({ url, caption: null })),
    }));

/** has_watermark says whether this account has ever been baselined. The client
 *  must NOT infer that from "every entry is_new": the feed is page-capped, so a
 *  returning user with more than a page of unseen entries looks identical to a
 *  brand-new account. Defaulted for older API responses. */
export const ChangelogFeedResponseSchema = z.object({
    entries: z.array(ChangelogFeedEntrySchema),
    has_watermark: z.boolean().default(false),
});

export type ChangelogFeedEntry = z.infer<typeof ChangelogFeedEntrySchema>;
export type ChangelogFeedScreenshot = z.infer<typeof ChangelogFeedScreenshotSchema>;
